import {
    REPLAY_OUTBOX_MAX_EVENTS, REPLAY_OUTBOX_TARGET_BYTES, REPLAY_OUTBOX_MAX_EVENT_BYTES,
} from './sessionReplayOutbox';

const MAX_PENDING_BYTES = 8 * 1024 * 1024;
const MAX_PENDING_EVENTS = 20_000;
const encoder = new TextEncoder();

interface PendingEvent {
    json?: string;
    unsaved?: unknown;
    bytes: number;
}

/** Bridges synchronous rrweb emits to asynchronous durable appends. Uploads are
 * deliberately outside this queue: a network outage must not grow capture heap.
 * Interrupt asks the owner to stop capture; every event already queued remains
 * available for persistence/retry. Nothing is evicted to meet a memory target. */
export class ReplayCaptureQueue {
    private pending: PendingEvent[] = [];
    private pendingBytes = 0;
    private largestEvent = 0;
    private running: Promise<void> | undefined;
    private failed: unknown;
    private interrupted = false;

    constructor(
        private readonly append: (events: readonly unknown[]) => Promise<unknown>,
        private readonly interrupt: (error: Error) => void,
    ) {}

    enqueue(event: unknown): void {
        let bytes: number;
        let json: string;
        try {
            json = JSON.stringify(event);
            if (json === undefined) throw new Error('Replay event is not serializable.');
            bytes = encoder.encode(json).byteLength;
            if (bytes > REPLAY_OUTBOX_MAX_EVENT_BYTES) throw new Error('Replay event exceeds local storage capacity.');
        } catch (error) {
            // Keep the unsaved event available to the owner, but do not call it
            // durably accepted or continue capturing into an unusable stream.
            this.pending.push({unsaved: event, bytes: 0});
            this.failed = error;
            this.stopCapture(error);
            return;
        }
        this.pending.push({json, bytes});
        this.pendingBytes += bytes;
        this.largestEvent = Math.max(this.largestEvent, bytes);
        // One exceptional snapshot is irreducible. Additional queued work has
        // its own allowance; a large valid initial snapshot alone is accepted.
        if (this.pending.length > MAX_PENDING_EVENTS || this.pendingBytes - this.largestEvent > MAX_PENDING_BYTES) {
            this.stopCapture(new Error('Replay storage is falling behind; capture was interrupted while pending events are saved.'));
        }
        this.start();
    }

    private stopCapture(error: unknown): void {
        if (this.interrupted) return;
        this.interrupted = true;
        this.interrupt(error instanceof Error ? error : new Error('Replay storage failed.'));
    }

    private start(): void {
        if (this.running || this.failed || !this.pending.length) return;
        this.running = this.drain().finally(() => {
            this.running = undefined;
            this.start();
        });
        // The stored failure is reported by flush(). Avoid an unhandled
        // rejection from the fire-and-forget synchronous emit boundary.
        void this.running.catch(() => {});
    }

    private async drain(): Promise<void> {
        while (this.pending.length) {
            let bytes = 2;
            let count = 0;
            for (const next of this.pending) {
                const newBytes = bytes + next.bytes + (count ? 1 : 0);
                if (count && (count === REPLAY_OUTBOX_MAX_EVENTS || newBytes > REPLAY_OUTBOX_TARGET_BYTES)) break;
                bytes = newBytes;
                count++;
            }
            const batch = this.pending.slice(0, count);
            try { await this.append(batch.map(next => next.json === undefined ? next.unsaved : JSON.parse(next.json))); }
            catch (error) {
                this.failed = error;
                this.stopCapture(error);
                throw error;
            }
            this.pending.splice(0, count);
            this.pendingBytes -= batch.reduce((sum, next) => sum + next.bytes, 0);
            this.largestEvent = this.pending.reduce((max, next) => Math.max(max, next.bytes), 0);
        }
    }

    /** Resolve only after every event accepted before/during this drain is durable. */
    async flush(): Promise<void> {
        if (this.failed) throw this.failed;
        this.start();
        if (this.running) await this.running;
        if (this.failed) throw this.failed;
    }

    /** Explicit retry after the owner has dealt with a storage failure. */
    async retry(): Promise<void> {
        this.failed = undefined;
        await this.flush();
    }

    /** Re-arm failure notification only after the owner has visibly paused,
     * retried every retained event, and is ready to resume capture. */
    resumeAfterRecovery(): void {
        if (this.failed || this.pending.length) throw new Error('Replay capture still has unsaved events.');
        this.interrupted = false;
    }

    get pendingCount(): number { return this.pending.length; }
    get pendingSerializedBytes(): number { return this.pendingBytes; }
    get isInterrupted(): boolean { return this.interrupted; }
}
